import React, { useEffect, useMemo, useState } from 'react';
import { MessageBar, MessageBarType, PrimaryButton, Spinner, TextField, Toggle } from '@fluentui/react';
import { getCrmCompetitionCatalog, getCrmEntityState, setCrmCompetition, setCrmMarketStatus } from '../api/prospectingApi';
import { competitionOptions, readCompetitionCatalog } from '../utils/competitionCatalog';

const CUSTOM_PROVIDER = '__one_other_provider__';

export default function CompetitionControl({ token, entityId, product, compact = false, onChanged }) {
  const [state, setState] = useState(null);
  const [providers, setProviders] = useState([]);
  const [labelSuggestions, setLabelSuggestions] = useState([]);
  const [provider, setProvider] = useState('');
  const [customProvider, setCustomProvider] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingOwn, setSavingOwn] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!entityId) {
      setState(null);
      setProvider('');
      setProviders([]);
      setLabelSuggestions([]);
      setCustomProvider(false);
      return undefined;
    }
    const controller = new AbortController();
    setState(null);
    setProvider('');
    setProviders([]);
    setLabelSuggestions([]);
    setCustomProvider(false);
    setLoading(true);
    setMessage(null);

    // Keep the facility state available even if fetching the catalogue fails.
    Promise.allSettled([
      getCrmEntityState(token, { id: entityId, product }, controller.signal),
      getCrmCompetitionCatalog(token, controller.signal),
    ]).then(([crmResult, catalogResult]) => {
      if (controller.signal.aborted) return;
      if (crmResult.status === 'fulfilled') {
        setState(crmResult.value);
        setProvider(String(crmResult.value?.competition?.provider || '').trim());
      }
      if (catalogResult.status === 'fulfilled') {
        const catalog = readCompetitionCatalog(catalogResult.value);
        setProviders(catalog.providers);
        setLabelSuggestions(catalog.labelSuggestions);
      }
      const errors = [crmResult, catalogResult]
        .filter((item) => item.status === 'rejected' && item.reason?.name !== 'AbortError')
        .map((item) => item.reason?.message || 'Błąd pobierania danych.');
      if (errors.length) setMessage({ type: 'error', text: errors.join(' ') });
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [token, entityId, product]);

  const own = Boolean(state?.market_status?.is_own_customer);
  const automaticOwn = Boolean(state?.market_status?.automatic);
  const unmappedLabels = (state?.sales?.labels || []).filter((label) => label?.type === 'other').map((label) => label?.name).filter(Boolean);
  const options = useMemo(
    () => competitionOptions(providers, labelSuggestions, provider),
    [providers, labelSuggestions, provider]
  );
  const providerIsListed = options.some((option) => option.key === provider);
  const selectedKey = customProvider ? CUSTOM_PROVIDER : (providerIsListed ? provider : '');
  const canSave = Boolean(provider.trim()) && !saving && !own && !loading && Boolean(state);

  function handleProviderSelection(event) {
    const choice = event.target.value;
    if (choice === CUSTOM_PROVIDER) {
      setCustomProvider(true);
      setProvider('');
    } else {
      setCustomProvider(false);
      setProvider(choice);
    }
    setMessage(null);
  }

  async function save() {
    if (!canSave) return;
    setSaving(true); setMessage(null);
    try {
      const response = await setCrmCompetition(token, { entity_id: entityId, product, provider: provider.trim(), push_to_pipedrive: true });
      setState((current) => ({ ...current, competition: response?.competition || current?.competition }));
      const status = response?.pipedrive?.status;
      setMessage({
        type: status === 'remote_wins' || status === 'conflict' ? 'warning' : 'success',
        text: status === 'added'
          ? 'Konkurencja zapisana w ONE i dodana jako etykieta w Pipedrive.'
          : status === 'already_present'
            ? 'Konkurencja jest już oznaczona w Pipedrive.'
            : status === 'remote_wins'
              ? `Pipedrive ma już konkurencję: ${response?.pipedrive?.provider}. ONE nie nadpisał istniejącej etykiety.`
              : status === 'not_linked'
                ? 'Konkurencja zapisana w ONE. Brak powiązania CRM, więc Pipedrive nie został zmieniony.'
                : 'Konkurencja zapisana.'
      });
      if (onChanged) onChanged();
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally { setSaving(false); }
  }

  async function changeOwn(_event, checked) {
    if (savingOwn || (automaticOwn && !checked)) return;
    setSavingOwn(true); setMessage(null);
    try {
      const response = await setCrmMarketStatus(token, { entity_id: entityId, product, is_own_customer: Boolean(checked) });
      setState((current) => ({
        ...current,
        market_status: response?.market_status || current?.market_status,
        competition: response?.competition ?? null,
      }));
      if (checked) { setProvider(''); setCustomProvider(false); }
      setMessage({
        type: 'success',
        text: checked
          ? 'Oznaczono jako nasz klient. Placówka nie będzie liczona do potencjalnego ARR/MRR, dzieci ani średnich scoringu.'
          : 'Usunięto ręczne oznaczenie naszego klienta.'
      });
      if (onChanged) onChanged();
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally { setSavingOwn(false); }
  }

  if (!entityId) return null;
  if (loading && !state) return compact ? null : <Spinner label="Pobieram status rynku i konkurencję…" />;
  return (
    <div className={`prospecting-competition-control ${compact ? 'is-compact' : ''} ${own ? 'is-own' : ''}`}>
      {product === 'kidplace' ? (
        <div className="prospecting-own-toggle">
          <Toggle
            label="Nasza placówka"
            checked={own}
            disabled={savingOwn || (automaticOwn && own)}
            onChange={changeOwn}
            onText="Tak"
            offText="Nie"
          />
          {automaticOwn ? <small>Status wynika z aktywnego powiązania klienta w ONE.</small> : null}
        </div>
      ) : null}
      <div className="prospecting-competition-choice">
        <label className="prospecting-competition-label">
          {!compact ? <span>Konkurencja</span> : null}
          <select
          className="prospecting-competition-select"
          value={selectedKey}
          disabled={own || loading || !state}
          onChange={handleProviderSelection}
          aria-label="Wybierz konkurenta"
        >
          <option value="">Brak / wybierz konkurencję</option>
          {options.map((option) => <option key={option.key} value={option.key}>{option.text}</option>)}
          <option value={CUSTOM_PROVIDER}>Inny dostawca — wpisz ręcznie…</option>
          </select>
        </label>
        {customProvider && !own ? (
          <TextField
            value={provider}
            onChange={(_event, value) => setProvider(value || '')}
            placeholder="Wpisz nazwę konkurenta"
            maxLength={96}
            ariaLabel="Nazwa innego dostawcy"
          />
        ) : null}
      </div>
      <PrimaryButton text={saving ? 'Zapisuję…' : 'Ustaw'} disabled={!canSave} onClick={save} />
      {!compact && state?.sales ? (
        <small>
          {state.sales.temperature ? `Etykieta: ${state.sales.temperature}` : 'Brak Hot/Warm/Cold'}
          {state.sales.preliminary_interest ? ' · wstępne zainteresowanie' : ''}
          {state.sales.last_lost_at ? ` · przegrana: ${String(state.sales.last_lost_at).slice(0, 10)}` : ''}
          {unmappedLabels.length ? ` · nieprzypisane etykiety Pipedrive: ${unmappedLabels.join(', ')}` : ''}
        </small>
      ) : null}
      {own ? <small>Konkurencja nie dotyczy placówek oznaczonych jako naszych klientów.</small> : null}
      {message ? <MessageBar messageBarType={message.type === 'error' ? MessageBarType.error : message.type === 'warning' ? MessageBarType.warning : MessageBarType.success}>{message.text}</MessageBar> : null}
    </div>
  );
}
